import { Component, computed, inject, input, output, signal } from '@angular/core';

import { InfoboxComponent } from '@core/components-infobox-legacy';
import type { AggregatedEvaluationsSkill } from '@core/evaluations-types';
import { AggregatedEvaluationsSkillStatus } from '@core/evaluations-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { JobSkillsSummaryDetailsBasicInfo } from '@core/positions-and-skills-types';
import { Theme } from '@core/theme-util';
import {
  BadgeComponent,
  ButtonComponent,
  EmptyStateComponent,
  HeadingComponent,
  IconComponent,
  RtTooltip,
} from '@core/user-interface-legacy';

import { EvaluationSkillComponent } from './evaluation-skill/evaluation-skill.component';
import { CandidateSkillsChartComponent } from '../../candidate-skills-chart/candidate-skills-chart.component';

@Component({
  selector: 'rt-candidate-offer-evaluation-question-skills',
  templateUrl: './candidate-offer-evaluation-question-skills.component.html',
  styleUrl: './candidate-offer-evaluation-question-skills.component.less',
  imports: [
    BadgeComponent,
    ButtonComponent,
    CandidateSkillsChartComponent,
    EmptyStateComponent,
    EvaluationSkillComponent,
    HeadingComponent,
    IconComponent,
    InfoboxComponent,
    RtTooltip,
    TranslatePipe,
  ],
})
export class CandidateOfferEvaluationQuestionSkillsComponent {
  private readonly i18n = inject(I18nService);

  protected readonly isCollapsed = signal(true);

  protected readonly summary = computed<JobSkillsSummaryDetailsBasicInfo>(() => {
    const skills = this.skills();
    const byStatus = (status: AggregatedEvaluationsSkillStatus) =>
      skills.filter(skill => skill.status === status);

    const aboveRequiredLevelSkills = byStatus(AggregatedEvaluationsSkillStatus.AboveRequiredLevel);
    const atRequiredLevelSkills = byStatus(AggregatedEvaluationsSkillStatus.AtRequiredLevel);
    const noRequiredLevelSkills = byStatus(AggregatedEvaluationsSkillStatus.NoRequiredLevel);

    return {
      aboveRequiredLevelSkills,
      atRequiredLevelSkills,
      belowRequiredLevelSkills: byStatus(AggregatedEvaluationsSkillStatus.BelowRequiredLevel),
      noEvaluatedSkills: byStatus(AggregatedEvaluationsSkillStatus.NoEvaluated),
      noRequiredLevelSkills,
      matchingSkillsCount: aboveRequiredLevelSkills.length + atRequiredLevelSkills.length,
      skillsCount: skills.length - noRequiredLevelSkills.length,
    };
  });

  protected readonly statusColumns = computed(() => {
    const summary = this.summary();

    const columns = [
      {
        status: AggregatedEvaluationsSkillStatus.AboveRequiredLevel,
        label: this.i18n.translate(
          'app.interview.summary.skills_summary.status.above_required_level',
        ),
        skills: summary.aboveRequiredLevelSkills,
      },
      {
        status: AggregatedEvaluationsSkillStatus.AtRequiredLevel,
        label: this.i18n.translate(
          'app.interview.summary.skills_summary.status.at_required_level_full',
        ),
        skills: summary.atRequiredLevelSkills,
      },
      {
        status: AggregatedEvaluationsSkillStatus.BelowRequiredLevel,
        label: this.i18n.translate(
          'app.interview.summary.skills_summary.status.below_required_level',
        ),
        skills: summary.belowRequiredLevelSkills,
      },
    ];

    if (summary.noEvaluatedSkills.length) {
      columns.push({
        status: AggregatedEvaluationsSkillStatus.NoEvaluated,
        label: this.i18n.translate('app.interview.summary.skills_summary.status.not_evaluated'),
        skills: summary.noEvaluatedSkills,
      });
    }

    return columns;
  });

  public questionTitle = input.required<string | null>();
  public answersCount = input.required<number>();
  public commentsCount = input(0);
  public skills = input.required<AggregatedEvaluationsSkill[]>();
  public theme = input<Theme | null>(null);
  public showPositionButton = input(true);

  public viewPosition = output();

  protected toggleCollapsed(): void {
    this.isCollapsed.update(isCollapsed => !isCollapsed);
  }
}
